import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  Agpm,
  AgpmDetails,
  AgpmHistoryDetailsMinimal,
  AgpmModeleRequest,
  BaseQueryParams,
  FlowableProcess,
  IResponse,
  IResponsePaginate,
  TaskHistory,
  WithMetadata,
} from '@/types';
import { Observable } from 'rxjs';

interface AgpmQuery extends BaseQueryParams {
  ref?: string;
  nom?: string;
  statusId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AgpmService {
  BASEURL = `${environment.api.agpmUrl}/agpms`;
  httpClient = inject(HttpClient);

  getAgpms(params?: AgpmQuery): Observable<IResponsePaginate<Agpm>> {
    return this.httpClient.get<IResponsePaginate<Agpm>>(`${this.BASEURL}`, { params: params as HttpParams });
  }

  getOneAgpm(id: number): Observable<WithMetadata<Agpm>> {
    return this.httpClient.get<WithMetadata<Agpm>>(`${this.BASEURL}/${id}`);
  }

  getOneAgpmDetails(id: number): Observable<AgpmDetails> {
    return this.httpClient.get<AgpmDetails>(`${this.BASEURL}/details/${id}`);
  }

  getAgpmVersion(id: number, version?: number): Observable<WithMetadata<Agpm>> {
    return this.httpClient.get<WithMetadata<Agpm>>(`${this.BASEURL}/${id}/${version}`);
  }

  addAgpm(agpm: FormData): Observable<WithMetadata<Agpm>> {
    return this.httpClient.post<WithMetadata<Agpm>>(`${this.BASEURL}`, agpm);
  }

  updateAgpm(id: number, agpm: FormData): Observable<IResponse<Agpm>> {
    return this.httpClient.put<IResponse<Agpm>>(`${this.BASEURL}/${id}`, agpm);
  }

  deleteAgpm(id: number, version?: number): Observable<IResponse<Agpm>> {
    return this.httpClient.delete<IResponse<Agpm>>(`${this.BASEURL}/${id}/${version}`);
  }

  // flowable process endpoints

  startWorkflow(agpmId: number): Observable<FlowableProcess> {
    return this.httpClient.post<FlowableProcess>(`${this.BASEURL}/flowable/start/${agpmId}`, {});
  }

  refreshAgpm(agpmId: number): Observable<IResponse<Agpm>> {
    return this.httpClient.post<IResponse<Agpm>>(`${this.BASEURL}/actualise/${agpmId}`, {});
  }

  getAgpmTaskHistory(processInstanceId: string): Observable<TaskHistory[]> {
    return this.httpClient.get<TaskHistory[]>(`${this.BASEURL}/flowable/task-history/${processInstanceId}`);
  }

  getAgpmHistoryDetailsMinimal(agpmId: number, params?: BaseQueryParams): Observable<IResponsePaginate<WithMetadata<AgpmHistoryDetailsMinimal>>> {
    return this.httpClient.get<IResponsePaginate<WithMetadata<AgpmHistoryDetailsMinimal>>>(`${this.BASEURL}/history/minimal/${agpmId}`, {
      params: params as HttpParams,
    });
  }

  downloadAgpmModel(modelRequest: AgpmModeleRequest): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL}/flowable/modele-agpm`, modelRequest, {
      responseType: 'blob',
    });
  }

  uploadAgpmModel(taskId: string, agpmFormData: FormData): Observable<IResponse<AgpmDetails>> {
    return this.httpClient.post<IResponse<AgpmDetails>>(`${this.BASEURL}/flowable/tasks/${taskId}/validate_with_model`, agpmFormData);
  }
}
